import { pack } from '@attain-apps/data-utils'
import { mapProperties } from '@attain-apps/object-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateObject } from './create-object'
import { CreateTuple } from './create-tuple'
import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Concatenates values together. This means summing numbers, concatenating strings or
 * lists, or merging objects.
 *
 * @param expressions Values to add together
 * @example
 * // Strings
 * { '++': [ 'a', 'b', 'c' ] }
 * // ->
 * 'abc'
 *
 * // Objects
 * {
 *   '++': [
 *     { '{}': { a: 1, b: 2 } },
 *     { '{}': { c: 3 } },
 *   ]
 * }
 * // ->
 * { a: 1, b: 2, c: 3 }
 *
 * // Lists
 * {
 *   '++': [
 *     { '[]': [ 1, 2 ] },
 *     { '[]': [ 3 ] },
 *   ]
 * }
 * // ->
 * [ 1, 2, 3 ]
 */
export interface Concatenate<out T = any> {
  /** {@link Concatenate} */
  readonly [Key.Concatenate]: T extends string
    ? readonly Expression<Concatenate.Part.String<T>>[]
    : T extends Object.Constraint
      ? readonly Expression<Concatenate.Part.Object<T>>[]
      : T extends readonly unknown[]
        ? readonly Expression<Concatenate.Part.List<T>>[]
        : never
}
export namespace Concatenate {
  export const isConcatenate: TypeChecker<Concatenate> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Concatenate>({
        [Key.Concatenate]: CommonArgs.isListOfExpressions as TypeChecker<
          Concatenate[Key.Concatenate]
        >,
      }),
    ).usingAlias('Fn.Concatenate')
  export const assertIsConcatenate: TypeAsserter<Concatenate> =
    TypeAsserter.Factory.from(isConcatenate)

  export namespace Part {
    export type String<T = any> = Extract<T, string>
    export type Object<T = any> = Partial<Extract<T, Object.Constraint>>
    // Distributes over the different lists in T (if it is a union), to have more accurate types
    export type List<T = any> = T extends readonly (infer U)[]
      ? readonly U[]
      : readonly any[]
  }

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.Concatenate,
    readonly expressionNode.Node[]
  > {
    constructor(
      arg: Node<T>['arg'],
      public readonly type: common.value.types.Type | undefined,
    ) {
      super(Key.Concatenate, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Concatenate>['expressionsInArgs'] = fn =>
    fn[Key.Concatenate]
  export const HELPER: FnHelper<Concatenate> = {
    // NOTE: for concatenate, this utility method is mostly just useful for
    // handling args of length 0 or 1
    createNode: (fn, context) => {
      const args = fn[Key.Concatenate]
      if (!args.length) {
        // If there is nothing to concatenate, we return nothing at all
        return new expressionNode.ForValue(undefined)
      }
      return args
        .map(expression => expressionNode.from(expression, context))
        .reduce((prevResult, arg) => {
          // First we try to figure out what type is appropriate based on the
          // previous result and current arg
          let type: common.value.types.Intrinsic | undefined
          // NOTE: the below logic attempts to mirror the logic of `||` from
          // Postgres
          if (
            [prevResult, arg].some(node =>
              common.value.types.Intrinsic.ForString.isForString(
                common.value.types.Intrinsic.from(node.type),
              ),
            )
          ) {
            // If either the previous result or current arg are fundamentally
            // text, then we are going to end up with text
            type = common.value.types.Intrinsic.Text
          } else if (
            [prevResult, arg].some(
              node =>
                common.value.types.Intrinsic.from(node.type) ===
                common.value.types.Intrinsic.List,
            )
          ) {
            // Otherwise, if either the previous result or current arg are
            // fundamentally a list, then we are going to end up with a list
            type = common.value.types.Intrinsic.List
          } else {
            // Otherwise, we just generalize the first type that we have
            type = common.value.types.Intrinsic.Generalized.from(
              prevResult.type ?? arg.type,
            )
          }

          if (
            type === common.value.types.Intrinsic.Text &&
            prevResult instanceof expressionNode.ForValue &&
            arg instanceof expressionNode.ForValue
          ) {
            // If we are returning text and both the previous result and current
            // arg are value expressions, then we simply concatenate them here
            // and now
            return new expressionNode.ForValue(
              (typeof prevResult.value === 'string'
                ? prevResult.value
                : (JSON.stringify(prevResult.value) ?? '')) +
                (typeof arg.value === 'string'
                  ? arg.value
                  : (JSON.stringify(arg.value) ?? '')),
            )
          } else if (type === common.value.types.Intrinsic.List) {
            // If we are returning a list...
            if (prevResult instanceof expressionNode.ForValue) {
              if (arg instanceof expressionNode.ForValue) {
                // If the previous result and current arg are both value
                // expressions, then we simply concatenate them here and now
                return new expressionNode.ForValue([
                  ...pack(prevResult.value as unknown),
                  ...pack(arg.value as unknown),
                ])
              } else if (arg instanceof CreateTuple.Node) {
                // If the previous result is a value expression and current arg
                // is a CreateTuple expression, then we create a new CreateTuple
                // expression that includes the previous result's value(s) at
                // the beginning
                return new CreateTuple.Node([
                  // If the value is a list, we add each item of the list to the
                  // tuple. Otherwise, we add the value itself to the tuple
                  ...pack(prevResult.value as unknown).map(
                    value => new expressionNode.ForValue(value),
                  ),
                  ...arg.arg,
                ])
              }
            } else if (prevResult instanceof CreateTuple.Node) {
              if (arg instanceof expressionNode.ForValue) {
                // If the previous result is a CreateTuple expression and
                // current arg is a value expression, then we create a new
                // CreateTuple expression that includes the current arg's
                // value(s) at the end
                return new CreateTuple.Node([
                  ...prevResult.arg,
                  // If the value is a list, we add each item of the list to the
                  // tuple. Otherwise, we add the value itself to the tuple
                  ...pack(arg.value as unknown).map(
                    value => new expressionNode.ForValue(value),
                  ),
                ])
              } else if (arg instanceof CreateTuple.Node) {
                // If the previous result and current arg are both CreateTuple
                // expressions, then we create a new CreateTuple expression with
                // the contents from both
                return new CreateTuple.Node([...prevResult.arg, ...arg.arg])
              } else if (
                arg.type !== undefined &&
                common.value.types.Intrinsic.from(arg.type) !==
                  common.value.types.Intrinsic.List
              ) {
                // If the previous result is a CreateTuple expression and the
                // current arg has a known type and that type is not a list,
                // then we create a new CreateTuple expression that adds the arg
                // as an item at the end of the tuple
                return new CreateTuple.Node([...prevResult.arg, arg])
              }
            } else if (
              prevResult.type !== undefined &&
              common.value.types.Intrinsic.from(prevResult.type) !==
                common.value.types.Intrinsic.List &&
              arg instanceof CreateTuple.Node
            ) {
              // If the previous result has a known type and that type is not a
              // list and the current arg is a CreateTuple expression, then we
              // create a new CreateTuple expression that adds the previous
              // result as an item at the beginning of the tuple
              return new CreateTuple.Node([prevResult, ...arg.arg])
            }
          } else if (type === common.value.types.Intrinsic.Object) {
            // If we are returning an object...
            if (prevResult instanceof expressionNode.ForValue) {
              if (arg instanceof expressionNode.ForValue) {
                // If the previous result and current arg are both value
                // expressions, then we simply concatenate them here and now
                return new expressionNode.ForValue({
                  ...prevResult.value,
                  ...arg.value,
                })
              } else if (arg instanceof CreateObject.Node) {
                // If the previous result is a value expression and current arg
                // is a CreateObject expression, then we create a new
                // CreateObject expression that includes the previous result's
                // properties at the beginning
                return new CreateObject.Node({
                  ...mapProperties(
                    prevResult.value,
                    value => new expressionNode.ForValue(value),
                  ),
                  ...arg.arg,
                })
              }
            } else if (prevResult instanceof CreateObject.Node) {
              if (arg instanceof expressionNode.ForValue) {
                // If the previous result is a CreateObject expression and
                // current arg is a value expression, then we create a new
                // CreateObject expression that includes the current arg's
                // properties at the end
                return new CreateObject.Node({
                  ...prevResult.arg,
                  ...mapProperties(
                    arg.value,
                    value => new expressionNode.ForValue(value),
                  ),
                })
              } else if (arg instanceof CreateObject.Node) {
                // If the previous result and current arg are both CreateObject
                // expressions, then we create a new CreateObject expression with
                // the contents from both
                return new CreateObject.Node({ ...prevResult.arg, ...arg.arg })
              }
            }
          }

          // If none of the above apply, then we use a Concatenate node.
          // However, if either of the parts are already a Conatenate node, then
          // we unpack the arguments of that Concatenate node so that we don't
          // end up with nested Concatenate expressions
          return new Node(
            [prevResult, arg].flatMap(node =>
              node instanceof Node ? node.arg : node,
            ),
            type,
          )
        })
    },
    expressionsInArgs,
    typeChecker: isConcatenate,
  }
}
